import { GridOptions } from 'ag-grid-community';
import { mergeDeepRight, pick } from 'ramda';
import React, {
  ComponentType,
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import { DefaultTheme, StyledComponent } from 'styled-components';

interface StylePreference {
  size: 'small' | 'middle' | 'large';
}

const defaultStylePreference: StylePreference = {
  size: 'middle',
};

type StyleContextType = {
  style: StylePreference;
  updateStyle: Dispatch<SetStateAction<StylePreference>>;
};

const StyleContext = createContext<StyleContextType | null>(null);

type Props = {
  children: JSX.Element[] | JSX.Element;
  initialStyle: StylePreference;
};

export const StylePreferenceProvider: FC<Props> = ({
  children,
  initialStyle = defaultStylePreference,
}) => {
  const [style, updateStyle] = useState<StylePreference>(initialStyle);

  return (
    <StyleContext.Provider value={{ style, updateStyle }}>
      {children}
    </StyleContext.Provider>
  );
};

export const withStylePreferenceProvider = <T extends GridOptions>(
  style = defaultStylePreference,
): ((Component: ComponentType<T>) => React.FC<T>) => {
  const withStyle = (Component: ComponentType<T>): FC<T> => {
    const Next: FC<T> = (props: T) => {
      return (
        <StylePreferenceProvider initialStyle={style}>
          <Component {...props} />
        </StylePreferenceProvider>
      );
    };

    return setDisplayName(
      wrapDisplayName(Component, 'StylePreference.Provider'),
    )(Next);
  };

  return withStyle;
};

export const useStyleContext = (): StyleContextType | null =>
  useContext(StyleContext);

export const withStylePreferenceConsumer = <
  T,
  K extends DefaultTheme,
  L extends Record<string, unknown>,
  M extends string | number | symbol,
  O extends Partial<StylePreference> & T,
>(
  Component: StyledComponent<ComponentType<T>, K, L, M>,
): FC<O> => {
  const Next: FC<O> = (props: O) => {
    const { style: contextStyle = defaultStylePreference } =
      useStyleContext() || {};

    const stylePropKeys = Object.keys(defaultStylePreference);
    const stylesFromParent = pick(
      stylePropKeys,
      props,
    ) as unknown as StylePreference;

    const styleProps: any = mergeDeepRight(contextStyle, stylesFromParent);

    return <Component {...props} {...styleProps} />;
  };

  return setDisplayName(wrapDisplayName(Component, 'StylePreference.Consumer'))(
    Next,
  );
};
